<template>
  <div class="chanel-login" :class="{'en-channel': currentLang === 'en'}" v-loading="loading">
    <!-- 主要内容 -->
    <div class="login-container">
      <!-- CHANEL Logo -->
      <div class="logo"></div>
      
      <!-- 标题和语言切换 -->
      <div class="header">
        <div class="title">{{ currentLang === 'zh' ? '查找账号' : 'LOG-IN' }}</div>
        <div class="language-selector" @click="toggleLanguage">
          {{ currentLang === 'zh' ? '中文' : 'EN' }}
          <svg class="lang-arrow" width="9" height="6" viewBox="0 0 10 6" fill="none" aria-hidden="true">
            <path fill-rule="evenodd" clip-rule="evenodd" d="M9.72348 1.13826L8.58523 1.42784e-05L4.86175 3.7235L1.13826 1.39529e-05L9.42346e-06 1.13826L4.86174 6L9.72348 1.13826Z" fill="#1D1D1D"/>
          </svg>
        </div>
      </div>
      
      <div v-if="!showQrCode">
        <!-- 登录方式切换 -->
        <div class="login-tabs">
          <div 
            class="tab-item" 
            :class="{ active: loginType === 'mobile' }"
            @click="switchLoginType('mobile')"
          >
            <span class="tab-text">
              {{ currentLang === 'zh' ? '手机号码' : 'MOBILE' }}
            </span>
          </div>
          <div 
            class="tab-item" 
            :class="{ active: loginType === 'email' }"
            @click="switchLoginType('email')"
          >
            <span class="tab-text">
              {{ currentLang === 'zh' ? '电子邮箱' : 'EMAIL' }}
            </span>
          </div>
        </div>
        
        <el-form 
          ref="loginForm"
          :model="formData" 
          :rules="formRules"
          class="form-container"
        >
          <!-- 手机号/邮箱输入 -->
          <el-form-item
            prop="account"
            class="input-group account-field"
            :class="{ 'is-filled': accountFocused || formData.account, focused: accountFocused }"
            :show-message="false"
          >
            <div class="input-content">
              <div class="input-field-top">
                <label class="input-label">{{ getInputLabel() }}</label>
                <div class="input-row">
                  <el-input
                    v-model="formData.account"
                    class="account-input"
                    @input="handleInputChange"
                    @focus="accountFocused = true"
                    @blur="accountFocused = false"
                  />
                </div>
              </div>
              <div class="input-line"></div>
              <div v-if="getFieldError('account')" class="field-error">{{ getFieldError('account') }}</div>
              <div v-else class="note">{{ getInputNote() }}</div>
            </div>
          </el-form-item>
          
          <!-- 验证码输入 -->
          <el-form-item
            prop="verificationCode"
            class="input-group"
            :class="{ 'is-filled': codeFocused || formData.verificationCode, focused: codeFocused }"
            :show-message="false"
          >
            <div class="input-content">
              <div class="input-field-top verification-field-top">
                <label class="input-label">
                  {{ currentLang === 'zh' ? '验证码*' : 'Verification code*' }}
                </label>
                <span class="get-code-btn" :class="{'get-code-highlight':isCanGetCode && countdown === 0}" @click="getVerificationCode">
                  <template v-if="countdown > 0">
                    {{ countdown }}s
                  </template>
                  <template v-else>
                    {{ currentLang === 'zh' ? '获取验证码' : 'Get code' }}
                  </template>
                </span>
                <div class="input-row verification-input-row">
                  <el-input
                    v-model="formData.verificationCode"
                    class="verification-input"
                    @focus="codeFocused = true"
                    @blur="codeFocused = false"
                    @change="verificationCodeChange"
                  />
                </div>
              </div>
              <div class="input-line"></div>
              <div v-if="getVerificationError()" class="field-error">{{ getVerificationError() }}</div>
            </div>
          </el-form-item>
          
          <!-- 隐私政策同意 -->
          <el-form-item prop="agreePrivacy" class="privacy-agreement">
            <el-checkbox v-model="formData.agreePrivacy" v-if="privacyData.text">
              <span class="agreement-text">
                <!-- 我同意将我的个人信息传输至中国大陆以外的香奈儿组织，并用于隐私政策中所述的目的。 -->
                <span v-for="(txt,idnex) in JSON.parse(privacyData.text || '[]')" :key="idnex" class="agreement-text-item">
                  <span v-if="txt.type ==='link'" class="link agreement-link" @click.stop.prevent="openLink(txt.url)">{{txt.val}}</span>
                  <span v-else class="agreement-normal">{{txt.val}}</span>
                </span>
              </span>
            </el-checkbox>
          </el-form-item>
          
          <!-- 提交按钮 -->
          <div class="submit-box">
            <el-button 
              class="submit-btn" 
              :class="{'keyboard-visible': isKeyboardVisible}"
              :disabled="!canSubmit"
              @click="handleSubmit"
            >
              {{ currentLang === 'zh' ? '提交' : 'SUBMIT' }}
            </el-button>
          </div> 
        </el-form>
      </div>

      <div v-else class="qrcode-container">
        <div class="qrcode-text">
          <div v-if="currentLang === 'zh'" class="qrcode-text-content">谢谢，请将此二维码提供给专属顾问</div>
          <div v-else class="qrcode-text-content">
            <div class="qrcode-text-line">Thank you, please show this QR code to</div>
            <div class="qrcode-text-line">your fashion advisor.</div>
          </div>
        </div>
        <img :src="qrCodeBase64" class="qrcode-img" alt="">
        <div class="qrcode-text-note">
          <div v-if="currentLang === 'zh'" class="qrcode-note-content">二维码将在 {{expireTime}} 后失效</div>
          <div v-else class="qrcode-note-content">
            <div class="qrcode-note-line">The QR code will expire after</div>
            <div class="qrcode-note-line">{{expireTime}}</div>
          </div>
        </div>
        <div class="refresh-code" @click="refreshQrCode">
          {{currentLang === 'zh' ?'点击刷新二维码' : 'REFRESH'}}
        </div>
        <div class="spacer" :class="{ 'en-spacer': currentLang === 'en' }"></div>
      </div>
    </div>

    <el-drawer
      title=""
      :visible.sync="drawerErrorVisible"
      :wrapperClosable="false"
      direction="btt"
      size="255px"
    >
      <div class="error-content">
        <div class="error-title">{{ currentLang === 'zh' ? '系统异常' : 'SYSTEM ERROR' }}</div>
        <div class="error-text">{{ currentLang === 'zh' ? '请稍后再试' : 'Please try again later' }}</div>
        <el-button type="primary" class="confirm-button" @click="drawerErrorVisible=false" block>
          {{ currentLang === 'zh' ? '确认' : 'CONFIRM' }}
        </el-button>
      </div>
    </el-drawer>
  </div>
</template>

<script>
import { setRemSize } from '@/utils/common'
import axios from "axios";
import { encryptAES } from '@/utils/crypto';
export default {
  name: 'ChanelLogin',
  data() {
    return {
      loading: false,
      currentLang: 'zh', // zh | en
      loginType: 'mobile', // mobile | email
      formData: {
        account: '',
        verificationCode: '',
        agreePrivacy: false
      },
      isCanGetCode:false,
      codeError:false,
      drawerErrorVisible:false,
      showQrCode:false,
      countdown: 0,
      countdownTimer: null,
      countdownEndTime: null,
      accountFocused: false,
      codeFocused: false,
      privacyData:{},
      viaQrKey: "",
      qrCodeBase64: "",
      expireTime: "",
      isKeyboardVisible: false,
      keyboardResizeHandler: null,
      codeErrorMessage: "",
      validationVersion: 0,
      codeErrorMap:{
        en:{
          incorrectCode:"Incorrect code",
          frequentRequest:"Request limit reached. Try again later.",
          requestFail:"Failed to send verification code. Try again later."
        },
        zh:{
          incorrectCode:"验证码错误",
          frequentRequest:"请求过于频繁，请稍后再试​",
          requestFail:"验证码发送失败，请稍后再试​"
        }
      }
    }
  },
  computed: {
    canSubmit() {
      return this.formData?.account && 
             this.isCanGetCode &&
             this.formData?.verificationCode?.length===6 && 
             this.formData?.agreePrivacy;
    },
    formRules() {
      return {
        account: [
          { required: true, message: this.getAccountRequiredMsg(), trigger: 'blur' },
          { validator: this.validateAccount, trigger: 'blur' }
        ],
        verificationCode: [
          // { required: true, message: this.getCodeRequiredMsg(), trigger: 'blur' }
          { validator: this.validateCode}
        ]
      };
    },
  },
  mounted() {
    setRemSize();
    window.addEventListener('resize', setRemSize);
    this.getRecollectionContent();
    this.initKeyboardDetection();
    this.initVisibilityChange();
  },
  beforeDestroy() {
    window.removeEventListener('resize', setRemSize);
    if (this.keyboardResizeHandler) {
      window.removeEventListener('resize', this.keyboardResizeHandler);
    }
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
    }
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
  },
  methods: {
    getRecollectionContent(){
      this.loading = true;
      axios.post(`${process.env.VUE_APP_CPC_API}/cpc/via/collection/getRecollectionContent`,{
        lang: this.currentLang === "zh" ? "zh-cn" : "en",
      }).then(res => {
        if (res?.data && res.data?.success && res.data?.object) {
          this.privacyData = res.data.object || {};
        }else{
          this.drawerErrorVisible = true;
        }
      }).catch(() => {
        this.drawerErrorVisible = true;
      }).finally(() => {
        this.loading = false;
      });
    },
    toggleLanguage() {
      this.currentLang = this.currentLang === 'zh' ? 'en' : 'zh';
      this.privacyData = {}
      this.getRecollectionContent()
      this.codeError = false
      // 清除表单验证
      this.$nextTick(() => {
        if (this.$refs.loginForm) {
          this.$refs.loginForm.clearValidate();
          this.bumpValidation();
        }
      });
    },
    
    switchLoginType(type) {
      this.loginType = type;
      this.formData.account = '';
      this.formData.verificationCode = '';
      this.isCanGetCode = false;
      // 清除表单验证
      this.$nextTick(() => {
        if (this.$refs.loginForm) {
          this.$refs.loginForm.clearValidate();
          this.bumpValidation();
        }
      });
    },
    
    getInputLabel() {
      if (this.loginType === 'mobile') {
        return this.currentLang === 'zh' ? '手机号码*' : 'CHANEL registered mobile number*';
      } else {
        return this.currentLang === 'zh' ? '邮箱*' : 'CHANEL registered email*';
      }
    },
    
    getInputNote() {
      if (this.loginType === 'mobile') {
        return this.currentLang === 'zh' ? 
          '请填写中国大陆以外的香奈儿账户所用手机号' : 
          'For accounts created outside Mainland China';
      } else {
        return this.currentLang === 'zh' ? 
          '请填写中国大陆以外的香奈儿账户所用邮箱' : 
          'For accounts created outside Mainland China';
      }
    },

    getFieldError(prop) {
      void this.validationVersion;
      const field = this.$refs.loginForm?.fields?.find(item => item.prop === prop);
      return field?.validateMessage || '';
    },

    bumpValidation() {
      this.validationVersion += 1;
    },

    getVerificationError() {
      if (this.codeError) {
        return this.codeErrorMessage;
      }
      return this.getFieldError('verificationCode');
    },
    
    getVerificationCode() {
      // 获取验证码逻辑
      if(this.isCanGetCode && this.countdown === 0){
        this.isCanGetCode = false;
        let params = {
          lang: this.currentLang === "zh" ? "zh-cn" : "en",
        }
        params[this.loginType] = encryptAES(this.formData.account)
        axios.post(`${process.env.VUE_APP_CPC_API}/cpc/via/collection/sendNotificationCode`,
          params, // 请求体数据
          {
            headers: {
              'via_qr_key': this.viaQrKey
            }
          }
        ).then(res => {
          this.viaQrKey = res.headers.via_qr_key || ""
          if (res?.data && res.data?.success && res.data?.object) {
            this.startCountdown();
          }else if(res?.data?.status === "000037"){
              this.showCodeMessage("frequentRequest")
          }else{
            this.showCodeMessage("requestFail")
          }
        }).catch(() => {
          this.showCodeMessage("requestFail")
        }).finally(()=>{
          this.isCanGetCode = true;
        })
      }
    },
    startCountdown() {
      this.countdown = 59;
      this.countdownEndTime = Date.now() + 59 * 1000;
      this.countdownTimer = setInterval(() => {
        const remainingTime = Math.max(0, Math.ceil((this.countdownEndTime - Date.now()) / 1000));
        this.countdown = remainingTime;
        if (this.countdown <= 0) {
          clearInterval(this.countdownTimer);
          this.countdownTimer = null;
          this.countdownEndTime = null;
        }
      }, 1000);
    },
    getAccountRequiredMsg() {
      return this.loginType === 'mobile' 
        ? (this.currentLang === 'zh' ? '请输入手机号码' : 'Please enter mobile number')
        : (this.currentLang === 'zh' ? '请输入邮箱地址' : 'Please enter email address');
    },
    
    getCodeRequiredMsg() {
      return this.currentLang === 'zh' ? '请输入验证码' : 'Please enter verification code';
    },
    
    validateAccount(rule, value, callback) {
      if (!value) {
        callback();
        this.bumpValidation();
        return;
      }
      
      if (this.loginType === 'mobile') {
        const mobileReg = /^1[3-9]\d{9}$/;
        if (!mobileReg.test(value)) {
          const msg = this.currentLang === 'zh' ? '请输入正确的手机号码格式' : 'Please enter a valid mobile number';
          callback(new Error(msg));
        } else {
          callback();
        }
      } else {
        const emailReg = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        if (!emailReg.test(value)) {
          const msg = this.currentLang === 'zh' ? '请输入正确的邮箱格式' : 'Please enter a valid email address';
          callback(new Error(msg));
        } else {
          callback();
        }
      }
      this.bumpValidation();
    }, 
    validateCode(rule, value, callback){
      if(this.codeError){
        callback(new Error(this.codeErrorMessage));
      }else{
        callback();
      }
      this.bumpValidation();
    },
    handleInputChange(value){
      if (this.loginType === 'mobile') {
        const mobileReg = /^1[3-9]\d{9}$/;
        if (mobileReg.test(value)) {
          this.isCanGetCode = true;
        }else{
          this.isCanGetCode = false;
        } 
      }else{
        const emailReg = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        if (emailReg.test(value)) {
          this.isCanGetCode = true;
        }else{
          this.isCanGetCode = false;
        }
      }
    },
    handleSubmit() {
      this.$refs.loginForm.validate((valid) => {
        if (valid) {
          console.log('提交表单', this.formData);

          this.loading = true;
          let params = {
            cbdt: this.formData.agreePrivacy,
            verifyCode: this.formData.verificationCode
          }
          params[this.loginType] = encryptAES(this.formData.account)
          axios.post(`${process.env.VUE_APP_CPC_API}/cpc/via/collection/submitCbdt`,
            params, // 请求体数据
            {
              headers: {
                'via_qr_key': this.viaQrKey
              }
            }
          ).then(res => {
            if (res?.data && res.data?.success && res.data?.object) {
              const {imageBase64,expireAt } = res.data.object
              this.qrCodeBase64 = `data:image/png;base64,${imageBase64}`
              this.expireTime = this.formatDate(expireAt)
              this.showQrCode = true
            }else if(res?.data?.status === "000036"){
              this.showCodeMessage("incorrectCode")
            }else{
              this.drawerErrorVisible = true
            }
          }).catch(() => {
            this.drawerErrorVisible = true
          }).finally(() => {
            this.loading = false;
          });
        }
      });
    },
    showCodeMessage(key){
      this.codeError = true
      this.codeErrorMessage = this.codeErrorMap[this.currentLang][key]
      this.$nextTick(()=>{
        this.$refs.loginForm.validateField('verificationCode')
        this.bumpValidation();
      })
    },
    verificationCodeChange(){
      this.codeError = false
      this.$nextTick(()=>{
        this.$refs.loginForm.validateField('verificationCode')
        this.bumpValidation();
      })
    },
    refreshQrCode(){
      this.loading = false;
      axios.post(`${process.env.VUE_APP_CPC_API}/cpc/via/collection/refreshQrCode`,
        {},
        {
          headers: {
            'via_qr_key': this.viaQrKey
          }
        }
      ).then(res => {
        if (res?.data && res.data?.success && res.data?.object) {
          const {imageBase64,expireAt } = res.data.object
          this.qrCodeBase64 = `data:image/png;base64,${imageBase64}`
          this.expireTime = this.formatDate(expireAt)
        }else{
          this.drawerErrorVisible = true
        }
      }).catch(() => {
        this.drawerErrorVisible = true
      }).finally(() => {
        this.loading = false;
      });
    },
    initVisibilityChange() {
      this.handleVisibilityChange = () => {
        if (!document.hidden && this.countdownEndTime) {
          // 页面重新可见时，立即更新倒计时
          const remainingTime = Math.max(0, Math.ceil((this.countdownEndTime - Date.now()) / 1000));
          this.countdown = remainingTime;
          if (this.countdown <= 0) {
            clearInterval(this.countdownTimer);
            this.countdownTimer = null;
            this.countdownEndTime = null;
          }
        }
      };
      document.addEventListener('visibilitychange', this.handleVisibilityChange);
    },
    formatDate(dateString) {
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      
      return `${year}/${month}/${day} ${hours}:${minutes}:${seconds}`;
    },
    initKeyboardDetection() {
      const initialHeight = window.innerHeight;
      this.keyboardResizeHandler = () => {
        const currentHeight = window.innerHeight;
        this.isKeyboardVisible = currentHeight < initialHeight * 0.75;
      };
      window.addEventListener('resize', this.keyboardResizeHandler);
    },
    openLink(url){
      window.open(url);
    }
  }
}
</script>

<style lang="scss" scoped>
.chanel-login {
  height: 100%;
  overflow-y: auto;
  background-color: #fff;
  font-family: "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", sans-serif;

  button,
  input,
  select,
  textarea,
  label {
    font-family: inherit;
    font-weight: inherit;
  }
  
  .top-bar {
    width: 100%;
    height: 0.8rem;
    background-color: #000;
  }
  
  .login-container {
    .logo {
      border-top: 8px solid #000;
      border-bottom: 1px solid #ECECEC;
      width: 100%;
      text-align: center;
      background: url("~@/assets/images/chanel-logo-mobile.svg")  no-repeat;
      background-position: 50%;
      background-size: 11.3rem 1.8rem;
      box-sizing: border-box;
      height: 6.3rem;
    }

    .link{
      text-decoration: underline;
      text-underline-offset: 0.2rem;
      cursor: pointer;
    }
    
    .header {
      display: flex;
      justify-content: center;
      align-items: center;
      margin: 2rem 0 2.4rem;
      position: relative;
      .title {
        font-size: 1.8rem;
        font-weight: 600;
        color: #1D1D1D;
        margin: 0;
        line-height: 2.3rem;
      }
      .language-selector {
        display: flex;
        align-items: flex-start;
        font-size: 1.4rem;
        line-height: 1.8rem;
        font-weight: 400;
        color: #1D1D1D;
        cursor: pointer;
        position: absolute;
        right: 2rem;
        
        .lang-arrow {
          flex-shrink: 0;
          width: 0.906rem;
          height: 0.559rem;
          margin-left: 0.4rem;
          margin-top: 0.7rem;
        }
      }
    }
    
    .login-tabs {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 3.2rem;
      border-bottom: 1px solid #ECECEC;
      padding: 0 8.8rem;
      
      .tab-item {
        flex: 0 0 auto;
        padding-bottom: 1.2rem;
        font-size: 1.4rem;
        line-height: 1.8rem;
        font-weight: 500;
        color: #A0A0A0;
        cursor: pointer;
        span{
          display: inline-block;
        }
        &.active {
          span{
            color: #1D1D1D;
            position: relative;
            &::after{
              content: '';
              display: block;
              width: 100%;
              height: 0.4rem;
              background: #000000;
              position: absolute;
              bottom: -1.2rem;
            }
          }
        }
      }
    }
    
    .form-container {
      padding: 0 2rem;
      ::v-deep .el-form-item {
        margin-bottom: 1.6rem;

        &.account-field {
          margin-bottom: 1.6rem;
        }
        
        .el-form-item__content {
          line-height: normal;
        }
      }
      
      .input-group {
        &.is-error {
          .input-line {
            border-bottom-width: 0.2rem;
            border-bottom-color: #CC0200;
          }
        }

        .input-content {
          position: relative;
        }

        .input-field-top {
          position: relative;
          height: 4.4rem;
        }

        .verification-field-top {
          display: flex;
          align-items: flex-start;
          
          .get-code-btn {
            position: absolute;
            bottom: 0;
            right: 0;
            z-index: 2;
            font-size: 1.4rem;
            font-weight: 300;
            color: #D2D2D2;
            cursor: pointer;
            white-space: nowrap;
            line-height: 2.4rem;
            text-align: right;
            -webkit-tap-highlight-color: transparent;
          }
          
          .get-code-highlight{
            color: #1D1D1D;
          }
        }
        
        .input-label {
          display: block;
          position: absolute;
          left: 0;
          right: 0;
          top: 2rem;
          height: 2.4rem;
          font-size: 1.4rem;
          font-weight: 300;
          color: #A0A0A0;
          line-height: 2.4rem;
          margin: 0;
          transition: font-size 0.2s ease, line-height 0.2s ease;
        }

        &.is-filled .input-label,
        &.focused .input-label {
          font-size: 1.2rem;
          line-height: 2rem;
          top: 0;
          height: 2rem;
        }

        &.is-filled .input-field-top,
        &.focused .input-field-top {
          height: 4.4rem;
        }
         
        .input-row {
          position: absolute;
          left: 0;
          right: 0;
          top: 2rem;
          height: 2.4rem;
          line-height: 2.4rem;
        }

        &.is-filled .input-row,
        &.focused .input-row {
          top: 2rem;
          height: 2.4rem;
        }

        .verification-field-top {
          .input-label {
            flex: 1;
            min-width: 0;
            padding-right: 10rem;
          }

          .verification-input-row {
            right: 0;
            left: 0;
            z-index: 1;
          }
        }

        .input-line {
          border-bottom: 0.1rem solid #D8D8D8;
          margin-top: 0.2rem;
        }

        .note,
        .field-error {
          font-size: 1.2rem;
          font-weight: 300;
          line-height: 2rem;
          margin-top: 0.2rem;
        }

        .note {
          color: #A0A0A0;
        }

        .field-error {
          color: #CC0200;
        }
        
        .account-input {
          ::v-deep .el-input__inner {
            border: none;
            border-radius: 0;
            padding: 0;
            font-family: "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", sans-serif;
            font-size: 1.4rem;
            font-weight: normal;
            background: transparent;
            height: 2.4rem;
            line-height: 2.4rem;
            color: #1D1D1D;
            -webkit-font-smoothing: antialiased;
            -moz-osx-font-smoothing: grayscale;
            
            &::placeholder {
              color: transparent;
            }
          }
        }
        
        .verification-input-row {
          .verification-input {
            flex: 1;
            
            ::v-deep .el-input__inner {
              border: none;
              border-radius: 0;
              padding: 0;
              font-family: "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", sans-serif;
              font-size: 1.4rem;
              font-weight: normal;
              background: transparent;
              height: 2.4rem;
              line-height: 2.4rem;
              color: #1D1D1D;
              -webkit-font-smoothing: antialiased;
              -moz-osx-font-smoothing: grayscale;
              
              &::placeholder {
                color: transparent;
              }
            }
          }
        }
        
        .get-code-btn {
          font-size: 1.4rem;
          font-weight: 300;
          color: #D2D2D2;
          cursor: pointer;
          white-space: nowrap;
          line-height: 2.4rem;
          text-align: right;
          -webkit-tap-highlight-color: transparent;
        }
      }
      
      .privacy-agreement {
        margin-top: 6rem;
        
        ::v-deep .el-checkbox {
          display: flex;
          align-items: flex-start;
          white-space: normal;
          margin-right: 0;
          &:last-child{
            margin-bottom: 0;
          }
          .el-checkbox__input{
            margin-top: 0.4rem;
            width: 1.3rem;
            .el-checkbox__inner{
              width: 1.2rem;
              height: 1.2rem;
              border: 0.2rem solid #1D1D1D;
              border-radius: 0;
              &::after{
                width: 0.3rem;
                height: 0.6rem;
                top: 0.05rem;
                left: 50%;
                transform: rotate(45deg) scaleY(0) translateX(-50%);
              }
            }
          }
          .el-checkbox__label{
            font-size: 1.2rem;
            font-weight: 300;
            line-height: 2rem;
            padding-left: 0.8rem;
            color:#1D1D1D;
            flex: 1;
            font-family: inherit;
            span {
              font-weight: 300;
            }
          }
        }

        /* 自定义checkbox选中样式：使用SVG矢量图，清晰无锯齿 */
        ::v-deep .el-checkbox__input.is-checked .el-checkbox__inner {
          background-image: url('../../../assets/images/icon-checked_vector.svg');
          background-size: contain;
          background-repeat: no-repeat;
          background-position: center;
          background-color: transparent;
          border: none;
          width: 1.3rem;
          height: 1.2rem;
        }

        ::v-deep .el-checkbox__input.is-checked .el-checkbox__inner::after {
          display: none;
        }
      }
      
      .submit-box{
        display: flex;
        justify-content: center;
      }
      .submit-btn {
        height:4.4rem;
        line-height: 4.4rem;
        font-size: 1.4rem;
        font-weight: 400;
        padding: 0;
        color: #F9F9F9;
        background-color: #000;
        width: 33.5rem;
        border-radius: 0;
        font-family: inherit;
        position: fixed;
        bottom: 4rem;
        transition: bottom 0.3s ease;

        &,
        &.is-disabled {
          font-family: inherit;
        }
        
        &.keyboard-visible {
          position: relative;
          bottom: auto;
          margin-top: 10rem;
        }
        
        &.is-disabled {
          background-color: #F9F9F9;
          color: #D2D2D2;
          border-color: #D8D8D8;
        }
      }
    }
    .qrcode-container{
      display: flex;
      flex-direction: column;
      align-items: stretch;
      position: absolute;
      left: 0;
      right: 0;
      width: 100%;
      /* 让二维码图片中心落在整页 50%：容器顶 = 50% - (图片上方 8rem + 图片高一半 7rem) */
      top: calc(50% - 15rem);
      box-sizing: border-box;
      .qrcode-text{
        color: #000000;
        font-size: 1.4rem;
        font-weight: 400;
        line-height: 2.4rem;
        text-align: center;
        width: 100%;
        max-width: 100%;
        padding: 0 2rem;
        box-sizing: border-box;
        min-height: 4.8rem;
        display: flex;
        flex-direction: column;
        justify-content: flex-end;
        align-items: center;

        .qrcode-text-content,
        .qrcode-text-line {
          width: 100%;
          max-width: 100%;
          text-align: center;
        }
      }
      .qrcode-img{
        display: block;
        align-self: center;
        width: 14rem;
        height: 14rem;
        margin: 3.2rem auto 1.6rem;
      }
      .qrcode-text-note{
        font-size: 1.2rem;
        font-weight: 300;
        color: #A0A0A0;
        line-height: 2rem;
        text-align: center;
        width: 100%;
        max-width: 100%;
        padding: 0 2rem;
        box-sizing: border-box;

        .qrcode-note-content,
        .qrcode-note-line {
          width: 100%;
          max-width: 100%;
          text-align: center;
          font-weight: 300;
        }
      }
      .refresh-code{
        align-self: center;
        cursor: pointer;
        color: #1D1D1D;
        font-family: "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", sans-serif;
        font-size: 1.4rem;
        font-weight: normal;
        line-height: 2.2rem;
        margin-top: 0.9rem;
        border-bottom: 1px solid #1D1D1D;
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
      }
      .spacer{
        height: 2rem;
        &.en-spacer{
          height: 0;
        }
      }
    }
  }

  &.en-channel {
    .title {
      font-family: 'ABChanel 2022 SemiBold', 'ABChanel 2022', sans-serif;
      font-weight: normal;
      font-size: 2.1rem !important;
      line-height: 2.3rem;
      letter-spacing: 0.03em;
      text-transform: uppercase;
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
    }

    .language-selector {
      font-family: 'ABChanel 2022 Regular', 'ABChanel 2022', sans-serif;
      font-weight: normal;
      font-size: 1.4rem;
      line-height: 1.6rem !important;
      letter-spacing: 0.03em;
      text-transform: uppercase;
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;

      .lang-arrow {
        align-self: center;
        margin-top: 0 !important;
      }
    }

    .login-tabs .tab-item {
      font-family: 'ABChanel Corpo 2022 Regular', 'ABChanel Corpo 2022', sans-serif;
      font-weight: normal;
      letter-spacing: 0.03em;
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
    }

    .tab-text {
      font-family: inherit;
      font-weight: normal;
      letter-spacing: inherit;
      text-transform: uppercase;
    }

    .form-container {
      ::v-deep .submit-btn {
        font-family: 'ABChanel Corpo 2022 Regular', 'ABChanel Corpo 2022', sans-serif !important;
        font-weight: normal !important;
        letter-spacing: 0.03em;
        text-transform: uppercase;
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;

        &,
        &.is-disabled,
        > span {
          font-family: 'ABChanel Corpo 2022 Regular', 'ABChanel Corpo 2022', sans-serif !important;
          font-weight: normal !important;
          letter-spacing: 0.03em;
        }
      }

      .account-input {
        ::v-deep .el-input__inner {
          font-family: Helvetica, "ABChanel Corpo 2022 Regular", "ABChanel Corpo 2022", sans-serif;
        }
      }

      .verification-input {
        ::v-deep .el-input__inner {
          font-family: Helvetica, "ABChanel Corpo 2022 Regular", "ABChanel Corpo 2022", sans-serif;
        }
      }
    }

    .qrcode-container {
      .refresh-code {
        font-family: "ABChanel Corpo 2022 Regular", "ABChanel Corpo 2022", sans-serif;
      }

      .qrcode-text {
        font-family: Helvetica, "ABChanel Corpo 2022 Regular", "ABChanel Corpo 2022", sans-serif !important;
        line-height: 2rem;

        .qrcode-text-content,
        .qrcode-text-line {
          font-family: Helvetica, "ABChanel Corpo 2022 Regular", "ABChanel Corpo 2022", sans-serif !important;
        }
      }

      .qrcode-text-note {
        font-family: Helvetica, "ABChanel Corpo 2022 Regular", "ABChanel Corpo 2022", sans-serif !important;
        line-height: 1.7rem;

        .qrcode-note-content,
        .qrcode-note-line {
          font-family: Helvetica, "ABChanel Corpo 2022 Regular", "ABChanel Corpo 2022", sans-serif !important;
        }
      }
    }

    .form-container {
      .input-group {
        .input-label {
          font-family: Helvetica, "ABChanel Corpo 2022 Regular", "ABChanel Corpo 2022", sans-serif;
        }

        .field-error {
          font-family: Helvetica, "ABChanel Corpo 2022 Regular", "ABChanel Corpo 2022", sans-serif;
        }

        .note {
          font-family: Helvetica, "ABChanel Corpo 2022 Regular", "ABChanel Corpo 2022", sans-serif;
        }
      }

      .verification-field-top {
        .get-code-btn {
          font-family: Helvetica, "ABChanel Corpo 2022 Regular", "ABChanel Corpo 2022", sans-serif;
        }
      }

      .privacy-agreement {
        ::v-deep .el-checkbox .el-checkbox__label {
          line-height: 1.7rem;
        }

        .agreement-text {
          font-family: Helvetica, "ABChanel Corpo 2022 Regular", "ABChanel Corpo 2022", sans-serif;
        }

        .agreement-text-item,
        .agreement-link,
        .agreement-normal {
          font-family: Helvetica, "ABChanel Corpo 2022 Regular", "ABChanel Corpo 2022", sans-serif;
        }
      }
    }
  }

  ::v-deep .el-drawer__wrapper{
    .el-drawer__header{
      padding: 20px;
      margin-bottom: 0;
      .el-drawer__close-btn{
        color: #1D1D1D;
        font-size: 18px;
      }
    }
    .error-content{
      display: flex;
      flex-direction: column;
      justify-items: center;
      align-items: center;
      font-family: inherit;
      .error-title{
        font-size: 18px;
        font-weight: 600;
        color: #1D1D1D;
        margin-bottom: 20px;
        line-height: 23px;
        font-family: inherit;
      }
      .error-text{
        font-size: 14px;
        font-weight: 400;
        color: #1D1D1D;
        line-height: 24px;
        font-family: inherit;
      }
      .confirm-button{
        margin-top: 40px;
        height:44px;
        line-height: 44px;
        font-size: 14px;
        font-weight: 400;
        padding: 0;
        color: #F9F9F9;
        background-color: #000;
        width: 335px;
        border-radius: 0;
        font-family: inherit;
        &:hover, &:focus {
          background-color: #1D1D1D;
          border-color: #1D1D1D;
        }
      }
    }
  }
}

</style>